/**
 * LGL Portal job menu — every project sidebar item, tagged for phone coverage.
 *
 * ready    → walks / taps work on the phone today
 * website  → opens the website page inside this app (Back to LGL Portal)
 * planned  → we intend a phone UI later
 * desktop  → stay on the desktop portal (grids, DBs, heavy admin)
 */

import { MENU_STRUCTURE } from '../../config/menuStructure';
import { MOBILE_APP_BACK_LABEL } from './appBranding';
import { FIELD_APP_DISCIPLINE_IDS } from './progressCatalog';

export const MOBILE_AVAILABILITY = {
  READY: 'ready',
  WEBSITE: 'website',
  PLANNED: 'planned',
  DESKTOP: 'desktop',
};

export const MOBILE_AVAILABILITY_LABEL = {
  ready: 'On phone',
  website: 'In this app',
  planned: 'Coming to phone',
  desktop: 'Desktop only',
};

export const MOBILE_AVAILABILITY_HINT = {
  ready: 'Tap to open on this phone.',
  website: `Opens the website page here. Use ${MOBILE_APP_BACK_LABEL} at the top.`,
  planned: 'Not on the phone yet — we plan to build it. Use the desktop portal for now.',
  desktop: 'Open this on the desktop portal. The phone view is not planned for this screen.',
};

/** Sidebar leaf id → phone discipline id (percent walk). */
export const MENU_ID_TO_DISCIPLINE = {
  'piping-insulation': 'piping',
  'mechanical-rotating-eqp': 'mechanical',
  structure: 'structural',
  hvac: 'hvac',
  'mechanical-ducts': 'ducts',
  boilermaker: 'boilermaker',
  electrical: 'electrical',
  civil: 'civil',
};

/**
 * Explicit coverage for project-menu leaves.
 * Anything not listed defaults to desktop.
 */
export const MOBILE_MENU_COVERAGE = {
  // Progress Tracking — phone walks
  'piping-insulation': { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'piping' },
  'mechanical-rotating-eqp': { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'mechanical' },
  structure: { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'structural' },
  hvac: { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'hvac' },
  'mechanical-ducts': { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'ducts' },
  boilermaker: { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'boilermaker' },
  electrical: { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'electrical' },
  civil: { availability: MOBILE_AVAILABILITY.READY, disciplineId: 'civil' },

  // Native job modules
  'piping-nde': {
    availability: MOBILE_AVAILABILITY.READY,
    moduleId: 'piping-nde',
    reason: 'Weld records, dates, and NDE types on this phone.',
  },
  'piping-testing': {
    availability: MOBILE_AVAILABILITY.READY,
    moduleId: 'piping-testing',
    reason: 'Test packages and status updates on this phone.',
  },
  'technical-photos': {
    availability: MOBILE_AVAILABILITY.WEBSITE,
    websiteHash: 'technical-photos',
    reason: 'Photo library on the website; the walk already attaches photos to a line.',
  },

  // Sensible phone candidates we have not built yet
  'technical-rfis': { availability: MOBILE_AVAILABILITY.PLANNED },
  'technical-issues': { availability: MOBILE_AVAILABILITY.PLANNED },
  'technical-meetings': { availability: MOBILE_AVAILABILITY.PLANNED },
  'schedule-checklists': { availability: MOBILE_AVAILABILITY.PLANNED },
  'qa-qc-punch-list-deficiency-list': { availability: MOBILE_AVAILABILITY.PLANNED },
  'qa-qc-material-receiving-inspection': { availability: MOBILE_AVAILABILITY.PLANNED },
  'control-chart': {
    availability: MOBILE_AVAILABILITY.READY,
    moduleId: 'control-chart',
    reason: 'Change log updates on this phone.',
  },
  'material-log': { availability: MOBILE_AVAILABILITY.PLANNED },
};

/** Extra percent trackers that live on the phone but are not yet sidebar leaves. */
export const EXTRA_MOBILE_TRACKERS = FIELD_APP_DISCIPLINE_IDS
  .filter((id) => !Object.values(MENU_ID_TO_DISCIPLINE).includes(id))
  .map((disciplineId) => ({
    id: `mobile-extra-${disciplineId}`,
    label: disciplineId.charAt(0).toUpperCase() + disciplineId.slice(1),
    availability: MOBILE_AVAILABILITY.READY,
    disciplineId,
    extra: true,
  }));

/**
 * Phone children that belong under a sidebar discipline, not beside it.
 * Labels match the desktop sidebar / in-page dropdowns — no extra "tracker".
 * Ducts stays a Progress Tracking sibling, same sequence as the desktop sidebar.
 */
export const DISCIPLINE_TRACKER_GROUPS = {
  'piping-insulation': {
    groupId: 'piping-tracker',
    label: 'Piping',
    modules: [
      ['piping-nde', 'NDE testing'],
      ['piping-testing', 'Testing'],
    ],
  },
  'mechanical-rotating-eqp': {
    groupId: 'mechanical-tracker',
    label: 'Mechanical',
    primaryLabel: 'Rotating Equipment',
    extraDisciplineIds: ['vessels', 'chutes', 'conveyors', 'skids'],
  },
};

const isProjectRoot = (item) => (
  item.requiresProject === true
  && item.department === 'projects'
  && !item.hideInProject
);

const leafCoverage = (id) => (
  MOBILE_MENU_COVERAGE[id] || { availability: MOBILE_AVAILABILITY.DESKTOP }
);

const annotateLeaf = (item) => {
  const coverage = leafCoverage(item.id);
  return {
    id: item.id,
    label: item.label,
    availability: coverage.availability,
    disciplineId: coverage.disciplineId || null,
    moduleId: coverage.moduleId || null,
    websiteHash: coverage.websiteHash || null,
    websitePath: coverage.websitePath || null,
    reason: coverage.reason || MOBILE_AVAILABILITY_HINT[coverage.availability],
    children: null,
  };
};

const annotateBranch = (item) => {
  const children = (item.subItems || []).map((child) => (
    child.subItems?.length ? annotateBranch(child) : annotateLeaf(child)
  ));
  return {
    id: item.id,
    label: item.label,
    icon: item.icon || null,
    availability: null,
    children,
  };
};

/** Department / estimate trees keep desktop nesting; coverage is home-route or desktop. */
const annotateHomeItem = (item) => {
  if (item.subItems?.length) {
    return {
      id: item.id,
      label: item.label,
      icon: item.icon || null,
      availability: null,
      children: item.subItems.map(annotateHomeItem),
    };
  }
  const coverage = HOME_MENU_COVERAGE[item.id] || {
    availability: MOBILE_AVAILABILITY.DESKTOP,
  };
  return homeLeaf(item.id, item.label, coverage);
};

/**
 * Accounting shortcuts that already have native phone screens.
 * Kept so older tests and callers can still find Time Tracker / Coding Structure.
 */
export const ACCOUNTING_HOME_SHORTCUTS = [
  {
    id: 'actuals',
    label: 'Time Tracker',
    route: 'time',
    reason: 'Weekly hours, plus daily crew hours if you are a crew head.',
  },
  {
    id: 'coding-structure',
    label: 'Coding Structure',
    route: 'coding',
    reason: 'Look up codes, cost types, and disciplines.',
  },
];

/**
 * Portal home menu on `/m` — department menus first, then jobs.
 * ready + homeRoute opens a phone screen; everything else stays desktop.
 */
export const HOME_MENU_COVERAGE = {
  'progress-tracker': {
    availability: MOBILE_AVAILABILITY.READY,
    homeRoute: 'jobs',
    reason: 'Pick a job, then add progress, comments, and photos.',
  },
  actuals: {
    availability: MOBILE_AVAILABILITY.READY,
    homeRoute: 'time',
    reason: 'Weekly hours, plus daily crew hours if you are a crew head.',
  },
  'coding-structure': {
    availability: MOBILE_AVAILABILITY.READY,
    homeRoute: 'coding',
    reason: 'Look up codes, cost types, and disciplines.',
  },
  'admin-panel': {
    availability: MOBILE_AVAILABILITY.READY,
    homeRoute: 'admin',
    reason: 'Same Admin Panel as the website — stays in this app.',
  },
};

const homeLeaf = (id, label, coverage) => ({
  id,
  label,
  availability: coverage.availability,
  homeRoute: coverage.homeRoute || null,
  disciplineId: null,
  moduleId: null,
  websiteHash: null,
  websitePath: null,
  reason: coverage.reason || MOBILE_AVAILABILITY_HINT[coverage.availability],
  children: null,
});

const isHomeDepartmentItem = (item) => (
  item.hideInProject === true
  && item.id !== 'department-selector'
  && item.id !== 'home'
);

const isEstimateRoot = (item) => (
  item.department === 'estimation'
  && item.hideInProject === true
  && item.id !== 'department-selector'
);

export const getHomeDepartments = ({ includeAdmin = false } = {}) => (
  (MENU_STRUCTURE.find((item) => item.id === 'department-selector')?.subItems || [])
    .filter((dept) => includeAdmin || dept.departmentId !== 'admin')
    .map((dept) => ({
      id: dept.departmentId,
      label: dept.label,
    }))
);

/**
 * Department menu after the landing dropdown. Progress Tracker goes to jobs.
 * Time Tracker, Coding Structure, and Admin open here. The rest say desktop.
 */
export const getHomeMobileMenu = ({ includeAdmin = false, departmentId = null } = {}) => {
  const departments = (MENU_STRUCTURE.find((item) => item.id === 'department-selector')?.subItems || [])
    .filter((dept) => includeAdmin || dept.departmentId !== 'admin')
    .filter((dept) => !departmentId || dept.departmentId === departmentId);

  return departments.map((dept) => {
    const children = [];

    if (dept.departmentId === 'projects') {
      children.push(homeLeaf(
        'progress-tracker',
        'Progress Tracker',
        HOME_MENU_COVERAGE['progress-tracker']
      ));
    }

    MENU_STRUCTURE
      .filter((item) => isHomeDepartmentItem(item) && item.department === dept.departmentId)
      .forEach((item) => {
        children.push(annotateHomeItem(item));
      });

    if (dept.departmentId === 'admin' && includeAdmin) {
      children.push(homeLeaf('admin-panel', 'Admin Panel', HOME_MENU_COVERAGE['admin-panel']));
    }

    if (!children.length) {
      children.push(homeLeaf(`${dept.departmentId}-desktop`, dept.label, {
        availability: MOBILE_AVAILABILITY.DESKTOP,
        reason: MOBILE_AVAILABILITY_HINT.desktop,
      }));
    }

    return {
      id: dept.id,
      label: dept.label,
      icon: null,
      availability: null,
      children,
    };
  });
};

const moduleLeaf = (id, label) => {
  const coverage = leafCoverage(id);
  return {
    id,
    label,
    availability: coverage.availability,
    disciplineId: null,
    moduleId: coverage.moduleId || null,
    websiteHash: null,
    websitePath: null,
    reason: coverage.reason || MOBILE_AVAILABILITY_HINT[coverage.availability],
    children: null,
  };
};

const extraTrackerLeaf = (disciplineId) => {
  const tracker = EXTRA_MOBILE_TRACKERS.find((item) => item.disciplineId === disciplineId);
  return {
    id: tracker?.id || `mobile-extra-${disciplineId}`,
    label: tracker?.label || disciplineId,
    availability: MOBILE_AVAILABILITY.READY,
    disciplineId,
    moduleId: null,
    websiteHash: null,
    websitePath: null,
    reason: MOBILE_AVAILABILITY_HINT.ready,
    children: null,
    extra: true,
  };
};

const wrapDisciplineGroup = (leaf, group) => ({
  id: group.groupId,
  label: group.label,
  icon: null,
  availability: null,
  children: [
    group.primaryLabel ? { ...leaf, label: group.primaryLabel } : leaf,
    ...(group.siblingLeaves || []),
    ...(group.modules || []).map(([id, label]) => moduleLeaf(id, label)),
    ...(group.extraDisciplineIds || []).map(extraTrackerLeaf),
  ],
});

const nestProgressTrackers = (children) => {
  const claimed = new Set();
  Object.values(DISCIPLINE_TRACKER_GROUPS).forEach((group) => {
    (group.siblingIds || []).forEach((id) => claimed.add(id));
  });

  const nested = [];
  children.forEach((child) => {
    const group = DISCIPLINE_TRACKER_GROUPS[child.id];
    if (group) {
      const siblingLeaves = (group.siblingIds || [])
        .map((id) => children.find((item) => item.id === id))
        .filter(Boolean);
      nested.push(wrapDisciplineGroup(child, { ...group, siblingLeaves }));
      return;
    }
    if (claimed.has(child.id)) return;
    nested.push(child);
  });
  return nested;
};

/**
 * Estimation sidebar after the landing estimate dropdown.
 */
export const getEstimateMobileMenu = () => (
  MENU_STRUCTURE
    .filter(isEstimateRoot)
    .map((item) => {
      if (item.subItems?.length) {
        return annotateHomeItem(item);
      }
      return {
        id: item.id,
        label: item.label,
        icon: item.icon || null,
        availability: null,
        children: [annotateHomeItem(item)],
      };
    })
);

/**
 * Project sidebar sections for the LGL Portal job menu, with coverage tags.
 * Same items and sequence as the desktop sidebar. Piping and Mechanical keep
 * their in-page subcategories underneath (NDE testing, Rotating Equipment, …).
 */
export const getProjectMobileMenu = () => {
  const sections = MENU_STRUCTURE
    .filter(isProjectRoot)
    .map((section) => {
      // Root items without children (SharePoint, Autodesk) are leaves.
      if (!section.subItems?.length) {
        return {
          id: section.id,
          label: section.label,
          icon: section.icon || null,
          availability: null,
          children: [annotateLeaf(section)],
        };
      }

      const annotated = annotateBranch(section);
      if (section.id === 'progressTrackers') {
        annotated.children = nestProgressTrackers(annotated.children || []);
      }
      return annotated;
    });

  return sections;
};

export const findMobileMenuGroup = (nodes, groupId) => {
  if (!groupId) return null;
  for (const node of nodes || []) {
    if (node.id === groupId && node.children?.length) return node;
    const found = findMobileMenuGroup(node.children, groupId);
    if (found) return found;
  }
  return null;
};

const walkLeaves = (nodes, out = []) => {
  nodes.forEach((node) => {
    if (node.children?.length) walkLeaves(node.children, out);
    else if (node.availability) out.push(node);
  });
  return out;
};

export const flattenMobileMenuLeaves = (sections = getProjectMobileMenu()) =>
  walkLeaves(sections);

/** Label for the job-menu Continue card — the leaf name, not the parent group. */
export const continueMenuLabel = (disciplineId, sections = getProjectMobileMenu()) => {
  if (!disciplineId) return null;
  const leaf = flattenMobileMenuLeaves(sections).find((item) => item.disciplineId === disciplineId);
  return leaf?.label || null;
};

export const summarizeMobileMenu = (sections = getProjectMobileMenu()) => {
  const leaves = flattenMobileMenuLeaves(sections);
  const summary = {
    ready: 0,
    website: 0,
    planned: 0,
    desktop: 0,
    total: leaves.length,
  };
  leaves.forEach((leaf) => {
    if (summary[leaf.availability] !== undefined) summary[leaf.availability] += 1;
  });
  return summary;
};

export const filterMobileMenuLeaves = (sections, availabilityFilter) => {
  if (!availabilityFilter || availabilityFilter === 'all') {
    return flattenMobileMenuLeaves(sections);
  }
  return flattenMobileMenuLeaves(sections).filter(
    (leaf) => leaf.availability === availabilityFilter
  );
};

export const websiteMenuUrl = (projectId, hash) => `/project/${projectId}#${hash}`;

/** Absolute website path for non-project shortcuts (Time Tracker, Coding Structure). */
export const websitePathUrl = (path) => {
  const value = String(path || '').trim();
  if (!value) return '/';
  return value.startsWith('/') ? value : `/${value}`;
};
